<script setup lang="ts">
import {
  computed,
  watch,
  type PropType,
  onBeforeMount,
  ref,
  type Ref,
} from "vue";
import { useI18n } from "vue-i18n";
import { PageLayout } from "@/layouts";
import {
  ButtonElement,
  LoadingState,
  RouteName,
  ScaleStatus,
  Unit,
  type ReducedWeighing,
} from "@/types";
import {
  CancelOrderDialog,
  IconLink,
  LoadingStateDisplay,
  PfrButton,
  ScaleCard,
  TitleHeader,
} from "@/components";
import { convertUnit, getScaleStatus } from "@/helpers";
import { useUserStore } from "@/stores";
import { useOrderItem } from "./useOrderItem.composable";

const props = defineProps({
  orderId: {
    type: String as PropType<string>,
    required: true,
  },
  itemId: {
    type: String as PropType<string>,
    required: true,
  },
});

const { order: orderItem, error, fetchOrderItem } = useOrderItem(props.itemId);
const userStore = useUserStore();

const { t } = useI18n();

// the following consts are used to keep track of the weight from the first
// received weighing since entering the screen.
// Because we aren`t able to access that last weighing, we need to "freeze" the weight
// as a new weighing comes in. The weight of the weighing is the difference between
// the initial weight reached (of the order) when entering the screen and the new weight reached.
const initialWeighings: Ref<ReducedWeighing[]> = ref([]);
const initialWeightReached: Ref<number | undefined> = ref(undefined);
const weighingReceived = ref(false);

onBeforeMount(async () => {
  await fetchOrderItem({
    orderId: props.orderId,
    orderItemId: props.itemId,
    withCheckWeighing: true,
  });
  initialWeighings.value = orderItem.value?.weighings || [];
  initialWeightReached.value = orderItem.value?.weightReached;
});

const currentWeighingId = ref<string | undefined>(undefined);
const weight = ref<number>(0);

const totalRemainingWeight = computed(
  () =>
    (orderItem.value?.targetWeight || 0) - (initialWeightReached.value || 0),
);

watch(orderItem, (newVal) => {
  // when we already received a weighing do no update weight
  if (!weighingReceived.value) {
    weight.value = newVal?.weightPromised || 0;
  }
  if (newVal?.weighings?.length === undefined) return;

  if (newVal.weighings.length === initialWeighings.value.length + 1) {
    currentWeighingId.value = newVal.weighings
      .filter((weighing) => !initialWeighings.value.includes(weighing))
      .at(0)?.id;
    const newWeighing = newVal.weighings.find(
      (weighing) => weighing.id === currentWeighingId.value,
    );
    weighingReceived.value = true;
    if (!newWeighing) return;
    weight.value = convertUnit(newWeighing?.weight, newWeighing?.unit, Unit.kg);
  }
});

const loadingState = computed(() => {
  if (error.value) {
    if (error.value.networkError) return LoadingState.NetworkError;
    return LoadingState.GraphqlError;
  }
  if (!orderItem.value) return LoadingState.Loading;
  return LoadingState.Success;
});

const convertedVehicleCapacity = computed(() => {
  if (!orderItem.value?.vehicle?.capacityInKg) return null;

  return convertUnit(
    orderItem.value.vehicle.capacityInKg,
    Unit.kg,
    orderItem.value.unit,
  );
});

const maxWeight = computed(() => {
  if (!orderItem.value) return 0;
  if (!convertedVehicleCapacity.value) return totalRemainingWeight.value;

  const min: number = Math.min(
    convertedVehicleCapacity.value,
    totalRemainingWeight.value,
  );

  return Math.max(min, 0);
});

const status = computed(() => {
  if (!orderItem.value) return ScaleStatus.Pending;
  return getScaleStatus(
    weight.value,
    convertedVehicleCapacity.value,
    totalRemainingWeight.value,
    convertUnit(50, Unit.kg, orderItem.value.unit),
  );
});
const canCancelOrder = computed(
  () =>
    !currentWeighingId.value &&
    status.value === ScaleStatus.Pending &&
    orderItem.value?.orderNumber,
);

const buttonText = computed(() => {
  if (
    status.value === ScaleStatus.VehicleOverloaded ||
    status.value === ScaleStatus.OrderOverloaded
  )
    return t("views.loadingStatus.ignoreOverload");
  return t("views.loadingStatus.confirm");
});

const nextPage = computed(() =>
  userStore.user?.loadAndGoQrCode &&
  orderItem.value?.firstVehicleWeighingItemPresent
    ? {
        name: RouteName.ScanQrCode,
        params: {
          orderId: props.orderId,
          itemId: props.itemId,
        },
        query: {
          weighingId: currentWeighingId.value,
        },
      }
    : {
        name: RouteName.WeighingConfirmation,
        params: { weighingId: currentWeighingId.value },
      },
);
</script>

<template>
  <PageLayout>
    <template #header>
      <TitleHeader :heading="t('views.loadingStatus.heading')">
        <template #left>
          <IconLink
            :to="{
              name: userStore.user?.loadAndGoQrCode
                ? RouteName.ScanQrCode
                : RouteName.OrderShow,
            }"
          />
        </template>
        <template #right>
          <CancelOrderDialog
            v-if="canCancelOrder"
            :order-number="orderItem?.orderNumber"
            :order-id="orderId"
          />
        </template>
      </TitleHeader>
    </template>

    <template #main>
      <LoadingStateDisplay :loading-state="loadingState">
        <template #default>
          <div v-if="orderItem" class="loading-status-view">
            <span class="loading-status-view--description">
              {{ t("views.loadingStatus.description") }}
            </span>
            <ScaleCard
              :status="status"
              :material="orderItem.material?.name || ''"
              :current-weight="weight"
              :current-weight-unit="orderItem.unit"
              :target-weight="maxWeight"
              :target-weight-unit="orderItem.unit"
              :order-number="orderItem.orderNumber"
            />
          </div>
        </template>
      </LoadingStateDisplay>
    </template>

    <template #mainFooter>
      <PfrButton
        :is="ButtonElement.RouterLink"
        v-if="currentWeighingId"
        :full-width="true"
        :to="nextPage"
      >
        {{ buttonText }}
      </PfrButton>
    </template>
  </PageLayout>
</template>

<style>
.loading-status-view--description {
  @apply text-18/28;

  display: block;
  margin-block-end: var(--spacing-64);
  text-align: center;
  color: var(--color-text-near-light);
}

.loading-status-view--call {
  margin-block-end: var(--spacing-24);
}
</style>
